import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, UserCheck, Plus, Search, Edit2, Trash2, X, Flame, Layers, Filter } from 'lucide-react';
import './CompanyManagement.css';
import './AdminUserManagement.css';

const AdminUserManagement = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const filterCompanyId = queryParams.get('companyId');

    const userRole = (localStorage.getItem('userRole') || '').toLowerCase();
    const isSuperAdmin = userRole === 'superadmin';
    const userCompanyId = localStorage.getItem('companyId') || '';

    const [admins, setAdmins] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingAdmin, setEditingAdmin] = useState(null);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const [formData, setFormData] = useState({
        username: '',
        password: '',
        email: '',
        companyId: '',
        role: isSuperAdmin ? 'Admin' : 'Quality Manager',
        subscribedDomains: []
    });

    const serverUrl = process.env.REACT_APP_SERVER_BASE_URL || 'http://localhost:8080';

    useEffect(() => {
        fetchAdminsAndCompanies();
    }, [filterCompanyId]);

    const fetchAdminsAndCompanies = async () => {
        setIsLoading(true);
        try {
            const adminUrl = filterCompanyId 
                ? `${serverUrl}/api/admin-users?companyId=${filterCompanyId}`
                : `${serverUrl}/api/admin-users`;

            const [adminRes, compRes] = await Promise.all([
                fetch(adminUrl),
                fetch(`${serverUrl}/api/companies`)
            ]);

            if (compRes.ok) {
                const compData = await compRes.json();
                setCompanies(compData);
            }

            if (adminRes.ok) {
                const adminData = await adminRes.json();
                setAdmins(adminData);
            }
        } catch (err) {
            console.log('Server unreachable, using local fallback data');
            const fallbackComps = [
                { _id: 'comp_1', name: 'Federal Steel Industries', code: 'FSI-001', maxUsers: 20 },
                { _id: 'comp_2', name: 'Apex Alloys Manufacturing', code: 'AAM-002', maxUsers: 20 }
            ];
            setCompanies(fallbackComps);
            let fallbackAdmins = [
                {
                    _id: 'adm_1',
                    username: 'admin_cu',
                    email: 'admin_cu@federalsteel.com',
                    role: 'Admin',
                    companyId: 'comp_1',
                    companyName: 'Federal Steel Industries',
                    subscribedDomains: [],
                    createdAt: new Date().toISOString()
                },
                {
                    _id: 'adm_2',
                    username: 'qm_steve',
                    email: 'quality@federalsteel.com',
                    role: 'Quality Manager',
                    companyId: 'comp_1',
                    companyName: 'Federal Steel Industries',
                    subscribedDomains: [],
                    createdAt: new Date().toISOString()
                },
                {
                    _id: 'adm_3',
                    username: 'op_mike',
                    email: 'operator@apexalloys.com',
                    role: 'Shop Floor Operator',
                    companyId: 'comp_2',
                    companyName: 'Apex Alloys Manufacturing',
                    subscribedDomains: [],
                    createdAt: new Date().toISOString()
                }
            ];
            if (filterCompanyId) {
                fallbackAdmins = fallbackAdmins.filter(a => (a.companyId?._id || a.companyId) === filterCompanyId);
            }
            setAdmins(fallbackAdmins);
        } finally {
            setIsLoading(false);
        }
    };

    const activeCompany = companies.find(c => c._id === filterCompanyId);

    const handleOpenModal = (admin = null, readOnly = false) => {
        setError('');
        setIsReadOnly(readOnly);
        if (admin) {
            setEditingAdmin(admin);
            setFormData({
                username: admin.username || '',
                password: '',
                email: admin.email || '',
                companyId: admin.companyId?._id || admin.companyId || '',
                role: admin.role || (isSuperAdmin ? 'Admin' : 'Quality Manager'),
                subscribedDomains: admin.subscribedDomains || []
            });
        } else {
            setEditingAdmin(null);
            const defaultCompId = (!isSuperAdmin && userCompanyId) 
                ? userCompanyId 
                : (filterCompanyId || (companies.length > 0 ? companies[0]._id : ''));
            setFormData({
                username: '',
                password: '',
                email: '',
                companyId: defaultCompId,
                role: isSuperAdmin ? 'Admin' : 'Quality Manager',
                subscribedDomains: []
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingAdmin(null);
        setIsReadOnly(false);
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!formData.username.trim() || !formData.email.trim() || !formData.companyId) {
            setError('Username, Email, and Company selection are required.');
            return;
        }

        if (!editingAdmin && !formData.password.trim()) {
            setError('Password is required for creating a user account.');
            return;
        }

        const selectedCompany = companies.find(c => c._id === formData.companyId);
        const maxUsersLimit = selectedCompany ? (selectedCompany.maxUsers !== undefined ? selectedCompany.maxUsers : 20) : 20;

        // Front-end check for company total user limit across ALL roles (including Admin)
        if (!editingAdmin && selectedCompany) {
            const existingCount = admins.filter(a => {
                const compId = a.companyId?._id || a.companyId;
                return compId === selectedCompany._id;
            }).length;

            if (existingCount >= maxUsersLimit) {
                setError(`User limit of ${maxUsersLimit} reached for ${selectedCompany.name} across all roles (including Admin). Upgrade user limit in Company Setup to add more users.`);
                return;
            }
        }

        const payload = {
            username: formData.username.trim(),
            email: formData.email.trim(),
            companyId: formData.companyId,
            role: formData.role || (isSuperAdmin ? 'Admin' : 'Quality Manager'),
            subscribedDomains: formData.subscribedDomains
        };
        if (formData.password.trim()) {
            payload.password = formData.password.trim();
        }

        try {
            const url = editingAdmin
                ? `${serverUrl}/api/admin-users/${editingAdmin._id}`
                : `${serverUrl}/api/admin-users`;
            const method = editingAdmin ? 'PUT' : 'POST';

            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const errData = await res.json();
                setError(errData.message || 'Failed to save admin account.');
                return;
            }

            const savedAdmin = await res.json();
            if (editingAdmin) {
                setEditingAdmin(savedAdmin);
                fetchAdminsAndCompanies();
                setIsReadOnly(true);
            } else {
                fetchAdminsAndCompanies();
                handleCloseModal();
            }
        } catch (err) {
            // Local state fallback update
            const companyName = selectedCompany ? selectedCompany.name : 'Organization';
            if (editingAdmin) {
                const updatedObj = { ...editingAdmin, ...payload, companyName };
                setEditingAdmin(updatedObj);
                setAdmins(prev => prev.map(a => a._id === editingAdmin._id ? updatedObj : a));
                setIsReadOnly(true);
            } else {
                setAdmins(prev => [{ _id: `adm_${Date.now()}`, ...payload, companyName, createdAt: new Date().toISOString() }, ...prev]);
                handleCloseModal();
            }
        }
    };

    const handleDeleteAdmin = async (adminId) => {
        if (!window.confirm('Are you sure you want to delete this Admin user account?')) return;

        try {
            await fetch(`${serverUrl}/api/admin-users/${adminId}`, { method: 'DELETE' });
            setAdmins(prev => prev.filter(a => a._id !== adminId));
        } catch (err) {
            setAdmins(prev => prev.filter(a => a._id !== adminId));
        }
    };

    const filteredAdmins = admins.filter(a => {
        const compName = a.companyId?.name || a.companyName || '';
        return (
            a.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
            a.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            compName.toLowerCase().includes(searchTerm.toLowerCase())
        );
    });

    return (
        <div className="superadmin-page">
            {/* Header */}
            <div className="page-header">
                <div className="page-header-left">
                    <button onClick={() => navigate(isSuperAdmin ? '/superadmin-dashboard' : '/admin-dashboard')} className="btn-secondary back-btn">
                        <ArrowLeft size={16} /> Dashboard
                    </button>
                    <div>
                        <h2 className="text-gradient" style={{ margin: 0, fontSize: '24px' }}>User Accounts</h2>
                    </div>
                </div>

                <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleOpenModal(null, false)}
                    className="btn-primary"
                >
                    <Plus size={18} /> Create Account
                </motion.button>
            </div>

            {/* Search Filter */}
            <div className="search-filter-bar" style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                <div className="search-input-wrapper">
                    <Search className="search-icon-inside" size={18} />
                    <input
                        type="text"
                        className="input-field"
                        style={{ paddingLeft: '42px' }}
                        placeholder="Search username, email, or company..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                {filterCompanyId && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(99, 102, 241, 0.12)', border: '1px solid rgba(99, 102, 241, 0.25)', padding: '6px 12px', borderRadius: '8px', fontSize: '13px' }}>
                        <Filter size={14} color="var(--primary-color)" />
                        <span>
                            Company: <strong>{activeCompany ? activeCompany.name : filterCompanyId}</strong>
                        </span>
                        {isSuperAdmin && (
                            <button 
                                onClick={() => navigate('/superadmin/admins')} 
                                className="icon-btn" 
                                title="Show All Accounts"
                                style={{ padding: '2px 4px', display: 'inline-flex', alignItems: 'center', gap: '2px', fontSize: '12px', color: 'var(--text-secondary)' }}
                            >
                                <X size={14} /> Clear
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Admins Table */}
            <div className="table-panel glass-panel">
                <table className="custom-table">
                    <thead>
                        <tr>
                            <th>User Name</th>
                            <th>Role</th>
                            <th>Email</th>
                            <th>Company / Organization</th>
                            <th>Subscribed Domains</th>
                            <th style={{ textAlign: 'right' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredAdmins.length === 0 ? (
                            <tr>
                                <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                                    No accounts found. Click "Create Account" to provision credentials.
                                </td>
                            </tr>
                        ) : (
                            filteredAdmins.map(a => {
                                const companyName = a.companyId?.name || a.companyName || 'Unassigned';
                                const companyCode = a.companyId?.code || '';
                                return (
                                    <tr key={a._id} className="clickable-row" onClick={() => handleOpenModal(a, true)}>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 600 }}>
                                                <UserCheck size={18} color="var(--primary-color)" />
                                                {a.username}
                                            </div>
                                        </td>
                                        <td>
                                            <span className="company-code-badge" style={{ background: 'rgba(99, 102, 241, 0.1)', color: 'var(--primary-color)', borderColor: 'rgba(99, 102, 241, 0.25)' }}>
                                                {a.role || 'Admin'}
                                            </span>
                                        </td>
                                        <td>{a.email}</td>
                                        <td>
                                            <span style={{ fontWeight: 600 }}>{companyName}</span>
                                            {companyCode && <span className="company-code-badge" style={{ marginLeft: '8px' }}>{companyCode}</span>}
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                                {a.subscribedDomains && a.subscribedDomains.length > 0 ? (
                                                    a.subscribedDomains.map(d => (
                                                        <span key={d} className={`domain-tag ${d.toLowerCase()}`}>
                                                            {d === 'CU' ? <Flame size={12} /> : <Layers size={12} />}
                                                            {d === 'CU' ? 'Copper (Cu)' : 'Iron (Fe)'}
                                                        </span>
                                                    ))
                                                ) : (
                                                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                                                        Via Subscription
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            <div className="action-btns" style={{ justifyContent: 'flex-end' }}>
                                                <button 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpenModal(a, false);
                                                    }} 
                                                    className="icon-btn" 
                                                    title="Edit User Account"
                                                >
                                                    <Edit2 size={16} />
                                                </button>
                                                <button 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteAdmin(a._id);
                                                    }} 
                                                    className="icon-btn delete" 
                                                    title="Delete User Account"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Read-Only Details / Edit Admin Modal */}
            <AnimatePresence>
                {isModalOpen && (
                    <div className="modal-overlay">
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="modal-card glass-panel"
                        >
                            <div className="modal-header">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <h3 className="text-gradient" style={{ margin: 0 }}>
                                        {isReadOnly ? 'User Details' : editingAdmin ? 'Edit Credentials' : 'Create User Account'}
                                    </h3>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    {isReadOnly && editingAdmin && (
                                        <button
                                            type="button"
                                            onClick={() => setIsReadOnly(false)}
                                            className="btn-primary"
                                            style={{ padding: '6px 14px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}
                                        >
                                            <Edit2 size={14} /> Edit Details
                                        </button>
                                    )}
                                    <button onClick={handleCloseModal} className="icon-btn" title="Close">
                                        <X size={20} />
                                    </button>
                                </div>
                            </div>

                            {isReadOnly ? (
                                <div className="read-only-details">
                                    <div className="read-only-grid">
                                        <div className="read-only-field form-full">
                                            <span className="read-only-label">User Name</span>
                                            <div className="read-only-value-large">
                                                <UserCheck size={20} color="var(--primary-color)" />
                                                <span>{formData.username}</span>
                                            </div>
                                        </div>

                                        <div className="read-only-field">
                                            <span className="read-only-label">Account Role</span>
                                            <div className="read-only-value">
                                                <span className="company-code-badge" style={{ background: 'rgba(99, 102, 241, 0.1)', color: 'var(--primary-color)', borderColor: 'rgba(99, 102, 241, 0.25)' }}>
                                                    {formData.role || 'Admin'}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="read-only-field">
                                            <span className="read-only-label">Email Address</span>
                                            <div className="read-only-value">{formData.email}</div>
                                        </div>

                                        <div className="read-only-field">
                                            <span className="read-only-label">Company / Organization</span>
                                            <div className="read-only-value">
                                                <span style={{ fontWeight: 600 }}>
                                                    {companies.find(c => c._id === formData.companyId)?.name || editingAdmin?.companyName || 'Unassigned'}
                                                </span>
                                                {companies.find(c => c._id === formData.companyId)?.code && (
                                                    <span className="company-code-badge" style={{ marginLeft: '8px' }}>
                                                        {companies.find(c => c._id === formData.companyId).code}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {formData.companyId && (
                                            <div className="read-only-field">
                                                <span className="read-only-label">Total Company User Quota</span>
                                                <div className="read-only-value">
                                                    <span className="company-code-badge" style={{ background: 'rgba(99, 102, 241, 0.1)', color: 'var(--secondary-color)', borderColor: 'rgba(99, 102, 241, 0.25)' }}>
                                                        {admins.filter(a => (a.companyId?._id || a.companyId) === formData.companyId).length} / {companies.find(c => c._id === formData.companyId)?.maxUsers !== undefined ? companies.find(c => c._id === formData.companyId).maxUsers : 20} Users (All Roles)
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        <div className="read-only-field form-full">
                                            <span className="read-only-label">Subscribed Domains</span>
                                            <div className="read-only-value" style={{ display: 'flex', gap: '8px', marginTop: '4px', alignItems: 'center' }}>
                                                {editingAdmin?.subscribedDomains && editingAdmin.subscribedDomains.length > 0 ? (
                                                    editingAdmin.subscribedDomains.map(d => (
                                                        <span key={d} className={`domain-tag ${d.toLowerCase()}`}>
                                                            {d === 'CU' ? <Flame size={12} /> : <Layers size={12} />}
                                                            {d === 'CU' ? 'Copper (Cu)' : 'Iron (Fe)'}
                                                        </span>
                                                    ))
                                                ) : (
                                                    <span style={{ fontSize: '13px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                                                        Assigned dynamically based on current company subscription.
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {editingAdmin?.createdAt && (
                                            <div className="read-only-field form-full">
                                                <span className="read-only-label">Created Date</span>
                                                <div className="read-only-value" style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                                                    {new Date(editingAdmin.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid var(--panel-border)' }}>
                                        <button type="button" onClick={handleCloseModal} className="btn-secondary">
                                            Close
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="form-grid">
                                    {isSuperAdmin ? (
                                        <div className="form-full">
                                            <label className="field-label">Select Company *</label>
                                            <select
                                                className="input-field"
                                                value={formData.companyId}
                                                onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
                                                required
                                            >
                                                <option value="" disabled>-- Select Company --</option>
                                                {companies.map(c => {
                                                    const used = admins.filter(a => (a.companyId?._id || a.companyId) === c._id).length;
                                                    const max = c.maxUsers !== undefined ? c.maxUsers : 20;
                                                    return (
                                                        <option key={c._id} value={c._id}>
                                                            {c.name} ({c.code}) — {used}/{max} Users Used (All Roles)
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                        </div>
                                    ) : (
                                        <div className="form-full">
                                            <label className="field-label">Company / Organization</label>
                                            <input
                                                type="text"
                                                className="input-field"
                                                value={companies.find(c => c._id === (formData.companyId || userCompanyId))?.name || localStorage.getItem('companyName') || 'Organization'}
                                                disabled
                                                style={{ opacity: 0.85, cursor: 'not-allowed', background: 'rgba(255, 255, 255, 0.05)', fontWeight: 600 }}
                                            />
                                        </div>
                                    )}

                                    {isSuperAdmin ? (
                                        <div>
                                            <label className="field-label">User Role</label>
                                            <input
                                                type="text"
                                                className="input-field"
                                                value={formData.role || 'Admin'}
                                                disabled
                                                style={{ opacity: 0.85, cursor: 'not-allowed', background: 'rgba(255, 255, 255, 0.05)', fontWeight: 600 }}
                                            />
                                        </div>
                                    ) : (
                                        <div>
                                            <label className="field-label">User Role *</label>
                                            <select
                                                className="input-field"
                                                value={formData.role}
                                                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                                required
                                            >
                                                <option value="Quality Manager">Quality Manager</option>
                                                <option value="Supervisor">Supervisor</option>
                                                <option value="Shop Floor Operator">Shop Floor Operator</option>
                                            </select>
                                        </div>
                                    )}

                                    <div>
                                        <label className="field-label">User Name *</label>
                                        <input
                                            type="text"
                                            className="input-field"
                                            placeholder="e.g. john_doe"
                                            value={formData.username}
                                            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                            required
                                        />
                                    </div>

                                    <div className="form-full">
                                        <label className="field-label">Email Address *</label>
                                        <input
                                            type="email"
                                            className="input-field"
                                            placeholder="e.g. user@company.com"
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            required
                                        />
                                    </div>

                                    <div className="form-full">
                                        <label className="field-label">
                                            {editingAdmin ? 'New Password (Optional)' : 'Password *'}
                                        </label>
                                        <input
                                            type="password"
                                            className="input-field"
                                            placeholder="Set login password"
                                            value={formData.password}
                                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                            required={!editingAdmin}
                                        />
                                    </div>

                                    {error && (
                                        <div className="form-full" style={{ color: 'var(--error-color)', fontSize: '13px', background: '#fee2e2', padding: '8px 12px', borderRadius: '6px' }}>
                                            {error}
                                        </div>
                                    )}

                                    <div className="form-full" style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '12px' }}>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (editingAdmin) {
                                                    setIsReadOnly(true);
                                                } else {
                                                    handleCloseModal();
                                                }
                                            }}
                                            className="btn-secondary"
                                        >
                                            Cancel
                                        </button>
                                        <button type="submit" className="btn-primary">
                                            {editingAdmin ? 'Save Changes' : 'Provision User Account'}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default AdminUserManagement;

